import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RIDDLED_IN_IMAGE_COST } from '../../../constants/games/riddledInPowerups';
import {
  generateRiddledInImage,
  listRiddledInImages,
} from '../../../utils/riddledInImageApi';
import { riddledInDebug } from '../../../utils/debug/riddledInDebug';
import RiddledInImagePowerupButton from './RiddledInImagePowerupButton';
import './RiddledInPowerups.css';

const RiddledInPowerups = ({
  user,
  coins = 0,
  gameId,
  phase,
  refreshCoins,
  onHasImageChange,
  showDemoLockedState = false,
}) => {
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  const canUsePowerups = Boolean(user) && Boolean(gameId) && phase !== 'loading';
  const canGenerate = phase === 'playing';
  const canAffordImage = Number(coins) >= RIDDLED_IN_IMAGE_COST;
  const shouldShowLockedHintButton = Boolean(showDemoLockedState) && !user;

  const latestImageUrl = useMemo(() => images?.[0]?.imageUrl || '', [images]);

  useEffect(() => {
    onHasImageChange?.(Boolean(latestImageUrl));
  }, [latestImageUrl, onHasImageChange]);

  // Load images for this game (so resume shows previous hint images).
  useEffect(() => {
    if (!canUsePowerups) return;

    let cancelled = false;
    (async () => {
      setImagesError('');
      setImagesLoading(true);
      riddledInDebug('powerups:list_images:start', { gameId: gameId || null });
      try {
        const res = await listRiddledInImages({ gameId });
        if (cancelled) return;
        setImages(Array.isArray(res.images) ? res.images : []);
        riddledInDebug('powerups:list_images:success', {
          gameId: res?.gameId || gameId || null,
          count: Array.isArray(res?.images) ? res.images.length : 0,
        });
      } catch (err) {
        if (cancelled) return;
        riddledInDebug('powerups:list_images:failed', {
          message: err?.message || String(err),
          code: err?.code || null,
          status: err?.status || null,
        });
        setImagesError(err?.message || 'Failed to load hint images.');
      } finally {
        if (!cancelled) setImagesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canUsePowerups, gameId]);

  const handleGenerateImage = useCallback(async () => {
    if (!canUsePowerups || !canGenerate) return;
    if (!canAffordImage) {
      setGenerateError('Not enough coins.');
      return;
    }
    if (isGenerating) return;

    setGenerateError('');
    setIsGenerating(true);
    riddledInDebug('powerups:generate_image:start', { gameId: gameId || null });
    try {
      const res = await generateRiddledInImage({ gameId });
      const imageUrl = res?.imageUrl;
      if (imageUrl) {
        setImages((prev) => [{ imageUrl, createdAt: new Date().toISOString() }, ...prev]);
      }
      await refreshCoins?.();
      riddledInDebug('powerups:generate_image:success', {
        hasImageUrl: Boolean(imageUrl),
        cost: res?.cost,
        coins: res?.coins,
        model: res?.model || null,
      });
    } catch (err) {
      riddledInDebug('powerups:generate_image:failed', {
        message: err?.message || String(err),
        code: err?.code || null,
        status: err?.status || null,
      });
      setGenerateError(err?.message || 'Image generation failed.');
      await refreshCoins?.();
    } finally {
      setIsGenerating(false);
    }
  }, [
    canUsePowerups,
    canGenerate,
    canAffordImage,
    isGenerating,
    gameId,
    refreshCoins,
  ]);

  if (shouldShowLockedHintButton) {
    return (
      <div className="riddled-in-powerups">
        <RiddledInImagePowerupButton
          onClick={() => {}}
          isProcessing={false}
          disabled
        />
        <div className="riddled-in-powerups-loading">
          Image hints are available for signed-up users only.
        </div>
      </div>
    );
  }

  if (!canUsePowerups) return null;

  return (
    <div className="riddled-in-powerups">
      <RiddledInImagePowerupButton
        onClick={handleGenerateImage}
        isProcessing={isGenerating}
        disabled={!canGenerate || imagesLoading || !canAffordImage}
      />

      {(generateError || imagesError) && (
        <div className="riddled-in-powerups-error">
          {generateError || imagesError}
        </div>
      )}

      {imagesLoading && (
        <div className="riddled-in-powerups-loading">Loading images...</div>
      )}

      {latestImageUrl && (
        <div className="riddled-in-powerups-image-card">
          <div className="riddled-in-powerups-image-label">🖼️ Hint image</div>
          <img
            className="riddled-in-powerups-image"
            src={latestImageUrl}
            alt="Hint for the secret word"
            loading="lazy"
          />
          <div className="riddled-in-powerups-image-note">
            Tip: This is optional and may or may not make the answer more obvious.
          </div>
        </div>
      )}
    </div>
  );
};

export default RiddledInPowerups;

